import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Shield,
  Users,
  BarChart3,
  Settings,
  LogOut,
  User,
  Brain,
  MessageSquare,
  FileText,
  DollarSign,
  ScrollText,
  MonitorSpeaker,
  Bot,
  Building2,
  Globe,
  Menu,
  ChevronDown,
  Package,
  TestTube2,
  Workflow
} from 'lucide-react';
import { usePlatformAdmin } from '@/contexts/PlatformAdminContext';
import { useTenantSelection } from '@/contexts/TenantSelectionContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@/components/LanguageSelector';

export default function PlatformAdminNavigation() {
  const { user, signOut } = usePlatformAdmin();
  const { selectedTenant, availableTenants, loading, selectTenant } = useTenantSelection();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation(['platformAdmin', 'common']);

  const globalManagementItems = [
    { label: t('platformAdmin:topNav.globalManagement.aiModels'), path: '/platform-admin/ai-management', icon: Brain },
    { label: t('platformAdmin:topNav.globalManagement.aiTemplates'), path: '/platform-admin/ai-templates', icon: FileText },
    { label: t('common:systemHealth'), path: '/platform-admin/system-health', icon: MonitorSpeaker },
    { label: t('common:users'), path: '/platform-admin/users', icon: Users },
    { label: t('platformAdmin:topNav.globalManagement.billingOverview'), path: '/platform-admin/billing', icon: DollarSign },
    { label: t('platformAdmin:topNav.globalManagement.systemLogs'), path: '/platform-admin/logging', icon: ScrollText },
    { label: t('platformAdmin:sections.templateLibrary.title'), path: '/platform-admin/templates', icon: FileText },
    { label: t('platformAdmin:topNav.globalManagement.conversationTesting'), path: '/platform-admin/conversation-testing', icon: TestTube2 },
  ];

  const tenantManagementItems = [
    { label: t('common:aiAgents'), path: 'ai-agents', icon: Bot },
    { label: t('platformAdmin:topNav.tenantManagement.canvas'), path: 'ai-agents/workflow-builder', icon: Workflow },
    { label: t('platformAdmin:topNav.tenantManagement.productsServices'), path: 'products-services', icon: Package },
    { label: t('common:whatsapp'), path: 'whatsapp', icon: MessageSquare },
    { label: t('common:analytics'), path: 'analytics', icon: BarChart3 },
    { label: t('platformAdmin:topNav.tenantManagement.logging'), path: 'logging', icon: ScrollText },
    { label: t('common:settings'), path: 'settings', icon: Settings },
  ];

  const handleTenantNavigation = (featurePath: string) => {
    if (selectedTenant) {
      navigate(`/platform-admin/tenant/${selectedTenant.id}/${featurePath}`);
    }
  };

  // Picking a tenant opens its management area; the context itself stays
  // navigation-free because the tenant layout re-selects from the URL.
  const handleTenantSelect = (tenant: Parameters<typeof selectTenant>[0]) => {
    selectTenant(tenant);
    if (tenant) navigate(`/platform-admin/tenant/${tenant.id}/ai-agents`);
  };

  const getTenantDropdownContent = () => {
    if (!selectedTenant) {
      return (
        <>
          <DropdownMenuLabel>{t('platformAdmin:topNav.selectATenant')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {loading ? (
            <DropdownMenuItem disabled>{t('common:loading')}</DropdownMenuItem>
          ) : availableTenants.length === 0 ? (
            <DropdownMenuItem disabled>{t('platformAdmin:topNav.noTenantsAvailable')}</DropdownMenuItem>
          ) : (
            availableTenants.map((tenant) => (
              <DropdownMenuItem
                key={tenant.id}
                onClick={() => handleTenantSelect(tenant)}
                className="flex flex-col items-start gap-1"
              >
                <div className="font-medium">{tenant.business_name}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <span>{t('platformAdmin:topNav.createdOn', { date: new Date(tenant.created_at).toLocaleDateString() })}</span>
                  <Badge variant="secondary" className="text-xs">
                    {t(`tenantStatus.${tenant.status}`, tenant.status)}
                  </Badge>
                </div>
              </DropdownMenuItem>
            ))
          )}
        </>
      );
    }

    return (
      <>
        <DropdownMenuLabel className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="font-medium">{selectedTenant.business_name}</span>
            <span className="text-xs text-muted-foreground">
              {t('platformAdmin:topNav.tenantManagementLabel')}
            </span>
          </div>
          <Badge variant="secondary" className="text-xs">
            {t(`tenantStatus.${selectedTenant.status}`, selectedTenant.status)}
          </Badge>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => selectTenant(null)}
          className="text-xs text-muted-foreground"
        >
          {t('platformAdmin:topNav.changeTenant')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => window.open(`/platform-admin/tenant/${selectedTenant.id}/ai-agents`, '_blank', 'noopener')}
          className="text-xs text-muted-foreground"
        >
          {t('platformAdmin:topNav.openInNewTab')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {tenantManagementItems.map((item) => (
          <DropdownMenuItem
            key={item.path}
            onClick={() => handleTenantNavigation(item.path)}
            className="flex items-center gap-2"
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </DropdownMenuItem>
        ))}
      </>
    );
  };

  

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const NavigationContent = () => (
    <div className="space-y-4">
      {/* Global Management Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">{t('platformAdmin:topNav.globalManagementHeading')}</span>
        </div>
        <div className="space-y-1">
          {globalManagementItems.map((item) => (
            <Button
              key={item.path}
              variant={isActive(item.path) ? "secondary" : "ghost"}
              size="sm"
              onClick={() => {
                navigate(item.path);
                setMobileMenuOpen(false);
              }}
              className="w-full justify-start gap-2"
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Button>
          ))}
        </div>
      </div>

      <Separator />

      {/* Tenant Management Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">{t('platformAdmin:topNav.tenantManagementHeading')}</span>
        </div>
        
        {!selectedTenant ? (
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground mb-2 px-2">{t('platformAdmin:topNav.selectATenantColon')}</div>
            {loading ? (
              <div className="px-2 py-1 text-xs text-muted-foreground">{t('common:loading')}</div>
            ) : availableTenants.length === 0 ? (
              <div className="px-2 py-1 text-xs text-muted-foreground">{t('platformAdmin:topNav.noTenantsAvailable')}</div>
            ) : (
              availableTenants.map((tenant) => (
                <Button
                  key={tenant.id}
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    handleTenantSelect(tenant);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full justify-start text-left h-auto py-2"
                >
                  <div className="flex flex-col items-start gap-1">
                    <div className="font-medium">{tenant.business_name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <span>{t('platformAdmin:topNav.createdOn', { date: new Date(tenant.created_at).toLocaleDateString() })}</span>
                      <Badge variant="secondary" className="text-xs">
                        {t(`tenantStatus.${tenant.status}`, tenant.status)}
                      </Badge>
                    </div>
                  </div>
                </Button>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <div className="px-2 py-2 border rounded-md bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="font-medium text-sm">{selectedTenant.business_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {t('platformAdmin:topNav.selectedTenantLabel')}
                  </span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {t(`tenantStatus.${selectedTenant.status}`, selectedTenant.status)}
                </Badge>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => selectTenant(null)}
              className="w-full justify-start text-xs text-muted-foreground"
            >
              {t('platformAdmin:topNav.changeTenant')}
            </Button>
            <Separator className="my-2" />
            {tenantManagementItems.map((item) => (
              <Button
                key={item.path}
                variant={isActive(`/platform-admin/tenant/${selectedTenant.id}/${item.path}`) ? "secondary" : "ghost"}
                size="sm"
                onClick={() => {
                  handleTenantNavigation(item.path);
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start gap-2"
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (!user) return null;

  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Brand */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold">{t('common:platformAdmin')}</span>
            </div>
          </div>

          {/* Desktop Navigation - Hidden on small screens */}
          {!isMobile && (
            <div className="hidden lg:flex lg:items-center lg:space-x-2">
              {/* Global Management Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    {t('platformAdmin:topNav.globalButton')}
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 bg-background/95 backdrop-blur">
                  {globalManagementItems.map((item) => (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="flex items-center gap-2"
                    >
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Separator orientation="vertical" className="h-6" />

              {/* Tenant Management Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {selectedTenant ? selectedTenant.business_name : t('platformAdmin:topNav.selectTenant')}
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 bg-background/95 backdrop-blur">
                  {getTenantDropdownContent()}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Mobile Navigation and User Profile */}
          <div className="flex items-center space-x-2">
            {/* Language Selector */}
            <LanguageSelector />

            {/* Mobile Menu */}
            {isMobile && (
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 bg-background/95 backdrop-blur">
                  <div className="flex items-center space-x-2 mb-6">
                    <Shield className="h-6 w-6 text-primary" />
                    <span className="text-lg font-bold">{t('common:platformAdmin')}</span>
                  </div>
                  <NavigationContent />
                </SheetContent>
              </Sheet>
            )}

            {/* User Profile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {user.email?.charAt(0).toUpperCase() || 'PA'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-background/95 backdrop-blur">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user.email}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {t('platformAdmin:topNav.platformAdministrator')}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/platform-admin/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  {t('platformAdmin:topNav.profileSettings')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/platform-admin/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  {t('platformAdmin:topNav.platformSettings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('common:signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}